/**
 * SA demo-tenant reset mutation hook.
 *
 * Endpoint: POST /admin/demo/reset
 *
 * Founder direction 2026-06-03 — see
 * ``backend/app/services/admin/demo_reset.py`` for the contract.
 *
 * On success the response carries ``login_url`` (always
 * ``https://app.certainhr.ai/demo/login``) which the SA hands to the
 * prospect along with the admin email. The temp password is minted
 * server-side and emailed to the prospect.
 *
 * On failure the response's ``detail`` carries an ``error`` discriminator
 * plus (for 500s) a ``phase`` token so the UI can surface which stage
 * failed (preflight, wipe, provision, seed_jds, seed_candidates,
 * seed_headshots, seed_rankings, audit_complete). ``recovery`` is always
 * "retry_reset" — the endpoint is idempotent.
 */
import { useMutation } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { ApiError } from "@/lib/apiError";
import { queryClient } from "@/lib/queryClient";

interface DemoResetBody {
  admin_email: string;
  admin_first_name: string;
  admin_last_name: string;
  confirm: "RESET DEMO";
}

interface DemoResetResponse {
  tenant_id: string;
  slug: "demo";
  admin_email: string;
  jds_seeded: number;
  candidates_seeded: number;
  rankings_seeded: number;
  initial_credits: number;
  audit_event_id: string;
  login_url: string;
}

export function useDemoReset() {
  return useMutation<DemoResetResponse, ApiError, DemoResetBody>({
    mutationFn: async (body) => {
      const r = await apiClient.post<DemoResetResponse>(
        "/admin/demo/reset",
        body,
      );
      return r.data;
    },
    onSuccess: () => {
      // The status hook reads the post-reset state — invalidate so the
      // form reloads with the new admin email + counts on next render.
      queryClient.invalidateQueries({ queryKey: ["admin", "demo", "status"] });
      // The tenants list shows the demo tenant alongside real tenants;
      // invalidate so the SA sees the new tenant id on the list page if
      // they navigate there.
      queryClient.invalidateQueries({ queryKey: ["admin", "tenants"] });
    },
  });
}
